import { useEffect, useState, useMemo } from "react";
import { DEFAULT_NAV_GROUPS } from "../constants/navCategories";
import {
  fetchAllCategoryData, setCategoryGroup, clearCategoryGroup,
  setProductCategory, clearProductCategory,
  buildMergedGroups, getOrphanCategories, getUnassignedProducts,
} from "../utils/categoryTree";

const PlusIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;

export default function AdminCustomCategories({ products }) {
  const [overrides, setOverrides] = useState({});
  const [productOverrides, setProductOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingCats, setSavingCats] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);

  const [selectedTop, setSelectedTop] = useState(DEFAULT_NAV_GROUPS[0]?.slug || null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [orphanPick, setOrphanPick] = useState("");
  const [addingOrphan, setAddingOrphan] = useState(false);
  const [productPick, setProductPick] = useState("");

  // Staged (not-yet-saved) moves — nothing is written to MySQL until the
  // admin clicks "Save changes".
  const [pendingCatMoves, setPendingCatMoves] = useState({});      // cat -> topSlug | "__reset__"
  const [pendingProductMoves, setPendingProductMoves] = useState({}); // productId -> cat | "__reset__"

  async function load() {
    setLoading(true);
    try {
      const { overrides, productOverrides } = await fetchAllCategoryData();
      setOverrides(overrides);
      setProductOverrides(productOverrides);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const mergedGroups = useMemo(
    () => buildMergedGroups({ allProducts: products, overrides, productOverrides }),
    [products, overrides, productOverrides]
  );

  // Every sub-category currently visible somewhere, grouped by top-level —
  // used to populate the "move this product to…" dropdown.
  const allSubsByGroup = useMemo(
    () => mergedGroups.map(g => ({ label: g.label, subs: g.subs })),
    [mergedGroups]
  );

  const orphanCats = useMemo(
    () => getOrphanCategories({ allProducts: products, overrides }),
    [products, overrides]
  );

  const unassignedProducts = useMemo(
    () => getUnassignedProducts({ allProducts: products, mergedGroups }),
    [products, mergedGroups]
  );

  const selectedGroup = mergedGroups.find(g => g.slug === selectedTop) || null;
  const selectedSubObj = selectedGroup?.subs.find(s => s.cat === selectedCat) || null;

  // Products staged (not yet saved) to move INTO the currently selected
  // sub-category from somewhere else, e.g. via the "unassigned products" picker.
  const pendingIncoming = useMemo(() => {
    if (!selectedSubObj) return [];
    const existingIds = new Set(selectedSubObj.products.map(p => p.id));
    return products.filter(p =>
      pendingProductMoves[String(p.id)] === selectedSubObj.cat && !existingIds.has(p.id)
    );
  }, [products, pendingProductMoves, selectedSubObj]);

  useEffect(() => {
    // If the selected category is no longer in the selected group (moved
    // away by someone else / another tab), clear the selection.
    if (selectedCat && selectedGroup && !selectedGroup.subs.some(s => s.cat === selectedCat)) {
      setSelectedCat(null);
    }
  }, [selectedGroup, selectedCat]);

  async function handleAddOrphan() {
    if (!orphanPick || !selectedTop) return;
    setAddingOrphan(true);
    try {
      await setCategoryGroup(orphanPick, selectedTop);
      setOrphanPick("");
      await load();
      setSelectedCat(orphanPick);
    } finally { setAddingOrphan(false); }
  }

  // ── Staging (dropdown just records the intent; nothing is written yet) ──
  function stageCatMove(cat, val) {
    setPendingCatMoves(prev => {
      const next = { ...prev };
      if (val) next[cat] = val; else delete next[cat];
      return next;
    });
  }
  function stageProductMove(productId, val) {
    setPendingProductMoves(prev => {
      const next = { ...prev };
      if (val) next[productId] = val; else delete next[productId];
      return next;
    });
  }

  async function handleSaveCatMoves() {
    setSavingCats(true);
    try {
      await Promise.all(Object.entries(pendingCatMoves).map(([cat, val]) =>
        val === "__reset__" ? clearCategoryGroup(cat) : setCategoryGroup(cat, val)
      ));
      setPendingCatMoves({});
      await load();
    } finally { setSavingCats(false); }
  }
  function handleDiscardCatMoves() { setPendingCatMoves({}); }

  function handleStageUnassignedProduct() {
    if (!productPick || !selectedSubObj) return;
    stageProductMove(String(productPick), selectedSubObj.cat);
    setProductPick("");
  }

  async function handleSaveProductMoves() {
    setSavingProducts(true);
    try {
      await Promise.all(Object.entries(pendingProductMoves).map(([productId, val]) => {
        if (val === "__reset__") return clearProductCategory(productId);
        const product = products.find(p => String(p.id) === String(productId));
        return product ? setProductCategory(product, val) : Promise.resolve();
      }));
      setPendingProductMoves({});
      await load();
    } finally { setSavingProducts(false); }
  }
  function handleDiscardProductMoves() { setPendingProductMoves({}); }

  if (loading) {
    return <div style={{ padding: 60, textAlign: "center" }}>
      <p style={{ color: "rgba(255,255,255,0.4)" }}>Loading category structure...</p>
    </div>;
  }

  return (
    <div>
      <div style={noteStyle}>
        Move any sub-category into a different top-level group, or move an individual product into a
        different sub-category. Everything else in a sub-category always shows the real SinaLite
        products that belong to it — nothing needs to be added manually. Moving something to a new
        spot removes it from the old one automatically, so it can never sit in two places at once.
        <br /><br />
        Top-level categories are fixed, and sub-categories are limited to SinaLite's own set of
        categories — here you can only control which top-level group each one appears under, and
        where individual products live.
      </div>

      <div style={colsWrap}>
        {/* Column 1: Top-level categories (fixed) */}
        <div style={colStyle}>
          <h4 style={colTitle}>Top-Level Categories</h4>
          <div style={listWrap}>
            {mergedGroups.map(g => (
              <div
                key={g.slug}
                onClick={() => { setSelectedTop(g.slug); setSelectedCat(null); }}
                style={itemStyle(selectedTop === g.slug)}
              >
                <span style={{ flex: 1 }}>{g.label}</span>
                <span style={countTag}>{g.subs.length}</span>
              </div>
            ))}
          </div>
          <p style={{ ...emptyText, marginTop: 10 }}>
            Top-level categories are fixed — new ones can't be added.
          </p>
        </div>

        {/* Column 2: Sub-categories under selected top */}
        <div style={colStyle}>
          <div style={colHeaderRow}>
            <h4 style={{ ...colTitle, marginBottom: 0 }}>Sub-Categories {selectedGroup && <span style={{ color: "var(--cyan)" }}>— {selectedGroup.label}</span>}</h4>
            {Object.keys(pendingCatMoves).length > 0 && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={handleDiscardCatMoves} disabled={savingCats} style={discardBtnStyle}>Discard</button>
                <button onClick={handleSaveCatMoves} disabled={savingCats} style={saveBtnStyle}>
                  {savingCats ? "Saving…" : `Save (${Object.keys(pendingCatMoves).length})`}
                </button>
              </div>
            )}
          </div>
          {!selectedGroup ? (
            <p style={emptyText}>Select a top-level category first.</p>
          ) : (
            <div style={listWrap}>
              {selectedGroup.subs.length === 0 && (
                <p style={emptyText}>This group has no sub-categories yet. Add one from below.</p>
              )}
              {selectedGroup.subs.map(s => {
                const pendingVal = pendingCatMoves[s.cat] || "";
                return (
                  <div key={s.cat} onClick={() => setSelectedCat(s.cat)} style={itemStyle(selectedCat === s.cat, !!pendingVal)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</div>
                      {pendingVal && <div style={{ fontSize: 10, color: "#fbbf24" }}>Unsaved — will {pendingVal === "__reset__" ? "reset to default" : "move"}</div>}
                      {!pendingVal && s.isMoved && <div style={{ fontSize: 10, color: "var(--cyan)" }}>Moved here</div>}
                    </div>
                    <span style={countTag}>{s.products.length}</span>
                    <select
                      value={pendingVal}
                      onClick={e => e.stopPropagation()}
                      onChange={e => stageCatMove(s.cat, e.target.value)}
                      style={moveSelectStyle}
                    >
                      <option value="" style={optionStyle}>Move…</option>
                      {DEFAULT_NAV_GROUPS.filter(g => g.slug !== selectedTop).map(g => (
                        <option key={g.slug} value={g.slug} style={optionStyle}>→ {g.label}</option>
                      ))}
                      {s.isMoved && s.defaultSlug && <option value="__reset__" style={optionStyle}>↺ Reset to default</option>}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {selectedTop && orphanCats.length > 0 && (
            <div style={addRowStyle}>
              <select value={orphanPick} onChange={e => setOrphanPick(e.target.value)} style={{ ...inputStyle, ...orphanSelectStyle }}>
                <option value="" style={optionStyle}>Select an unassigned category…</option>
                {orphanCats.map(c => <option key={c} value={c} style={optionStyle}>{c}</option>)}
              </select>
              <button onClick={handleAddOrphan} disabled={addingOrphan || !orphanPick} style={addBtnStyle} title="Add to this group">
                <PlusIcon />
              </button>
            </div>
          )}
        </div>

        {/* Column 3: Products in the selected sub-category */}
        <div style={{ ...colStyle, flex: 1.4 }}>
          <div style={colHeaderRow}>
            <h4 style={{ ...colTitle, marginBottom: 0 }}>Products {selectedSubObj && <span style={{ color: "var(--cyan)" }}>— {selectedSubObj.label}</span>}</h4>
            {Object.keys(pendingProductMoves).length > 0 && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={handleDiscardProductMoves} disabled={savingProducts} style={discardBtnStyle}>Discard</button>
                <button onClick={handleSaveProductMoves} disabled={savingProducts} style={saveBtnStyle}>
                  {savingProducts ? "Saving…" : `Save (${Object.keys(pendingProductMoves).length})`}
                </button>
              </div>
            )}
          </div>
          {!selectedSubObj ? (
            <p style={emptyText}>Click a sub-category to view its products.</p>
          ) : (
            <>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>
                {selectedSubObj.products.length} product{selectedSubObj.products.length !== 1 ? "s" : ""} — coming directly from SinaLite's real category "{selectedSubObj.cat}".
              </p>
              <div style={{ ...listWrap, maxHeight: 420 }}>
                {selectedSubObj.products.length === 0 && pendingIncoming.length === 0 && (
                  <p style={emptyText}>There are no live products in this category right now.</p>
                )}
                {pendingIncoming.map(p => (
                  <div key={p.id} style={itemStyle(false, true)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: "#fbbf24" }}>Unsaved — will move here (from {p.category})</div>
                    </div>
                    <button onClick={() => stageProductMove(String(p.id), "")} style={discardBtnStyle}>Cancel</button>
                  </div>
                ))}
                {selectedSubObj.products.map(p => {
                  const isOverridden = !!productOverrides[String(p.id)];
                  const pendingVal = pendingProductMoves[String(p.id)] || "";
                  return (
                    <div key={p.id} style={itemStyle(false, !!pendingVal)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                        {pendingVal && <div style={{ fontSize: 10, color: "#fbbf24" }}>Unsaved — will {pendingVal === "__reset__" ? "reset to original" : "move"}</div>}
                        {!pendingVal && isOverridden && <div style={{ fontSize: 10, color: "var(--cyan)" }}>Moved here (real category: {p.category})</div>}
                      </div>
                      <select
                        value={pendingVal}
                        onChange={e => stageProductMove(String(p.id), e.target.value)}
                        style={moveSelectStyle}
                      >
                        <option value="" style={optionStyle}>Move…</option>
                        {allSubsByGroup.map(g => (
                          <optgroup key={g.label} label={g.label} style={optionStyle}>
                            {g.subs.filter(s => s.cat !== selectedSubObj.cat).map(s => (
                              <option key={s.cat} value={s.cat} style={optionStyle}>{s.label}</option>
                            ))}
                          </optgroup>
                        ))}
                        {isOverridden && <option value="__reset__" style={optionStyle}>↺ Reset to original ({p.category})</option>}
                      </select>
                    </div>
                  );
                })}
              </div>

              {unassignedProducts.length > 0 && (
                <div style={addRowStyle}>
                  <select value={productPick} onChange={e => setProductPick(e.target.value)} style={{ ...inputStyle, ...orphanSelectStyle }}>
                    <option value="" style={optionStyle}>Select an unassigned product…</option>
                    {unassignedProducts.map(p => (
                      <option key={p.id} value={p.id} style={optionStyle}>{p.name} ({p.category})</option>
                    ))}
                  </select>
                  <button onClick={handleStageUnassignedProduct} disabled={!productPick} style={addBtnStyle} title="Add to this sub-category">
                    <PlusIcon />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────
const noteStyle = {
  background: "rgba(0,170,221,0.06)", border: "1px solid rgba(0,170,221,0.2)",
  color: "rgba(255,255,255,0.7)", borderRadius: 10, padding: "12px 16px",
  fontSize: 13, lineHeight: 1.7, marginBottom: 20,
};
const colsWrap = { display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" };
const colStyle = {
  flex: 1, minWidth: 280, minHeight: 0,
  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: 12, padding: 16, overflow: "hidden", boxSizing: "border-box",
};
const colTitle = { color: "#fff", fontSize: 14, marginBottom: 12 };
const listWrap = { display: "flex", flexDirection: "column", gap: 6, maxHeight: 340, overflowY: "auto", minWidth: 0 };
const emptyText = { color: "rgba(255,255,255,0.3)", fontSize: 12, padding: "10px 0" };
function itemStyle(active, pending) {
  return {
    display: "flex", alignItems: "center", gap: 8,
    background: active ? "rgba(0,170,221,0.12)" : "rgba(255,255,255,0.04)",
    border: "1px solid " + (pending ? "#fbbf24" : (active ? "var(--cyan)" : "rgba(255,255,255,0.08)")),
    borderRadius: 8, padding: "8px 10px", fontSize: 13, color: "#fff",
    cursor: "pointer", minWidth: 0,
  };
}
const colHeaderRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12, flexWrap: "wrap" };
const saveBtnStyle = {
  background: "var(--cyan)", border: "none", color: "#fff", borderRadius: 8,
  padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
};
const discardBtnStyle = {
  background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)",
  borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
};
const countTag = {
  background: "rgba(0,170,221,0.15)", color: "var(--cyan)",
  fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20, flexShrink: 0,
};
const addRowStyle = { display: "flex", gap: 6, marginTop: 12, minWidth: 0 };
const inputStyle = {
  flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8, color: "#fff", padding: "8px 10px", fontSize: 13, outline: "none",
};
const orphanSelectStyle = {
  minWidth: 0, width: "100%", boxSizing: "border-box",
  background: "#151a23", color: "#fff", colorScheme: "dark",
};
const optionStyle = { background: "#151a23", color: "#fff" };
const addBtnStyle = {
  background: "var(--cyan)", border: "none", color: "#fff", borderRadius: 8,
  width: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
};
const moveSelectStyle = {
  background: "#151a23", border: "1px solid rgba(255,255,255,0.15)",
  color: "rgba(255,255,255,0.7)", borderRadius: 6, fontSize: 11, padding: "3px 4px",
  maxWidth: 90, minWidth: 0, flexShrink: 0, cursor: "pointer", colorScheme: "dark",
};